/**
 * Commercial Invoice Template
 * Returns HTML string for the Commercial Invoice page
 */
export const generateCommercialInvoiceHTML = (data) => {
    const {
        shipper = {},
        destination = {},
        products = [],
        invoiceNo = '',
        commodityInvoiceNo = '',
        exportReason = '',
        countryOfOrigin = '',
        declarationStatement = '',
        currencyType = 'USD',
        date = new Date().toLocaleDateString('en-GB'),
        trackingNumber = 'N/A',
        freightValue = '',
        insuranceValue = '',
        shipmentInsuranceDeclaredValue = ''
    } = data;

    // Use only commodityInvoiceNo as per user request to "show nothing if not given"
    const displayInvoiceNo = commodityInvoiceNo || '';

    let subtotal = 0;
    const itemsHTML = products.map((p, idx) => {
        const qty = parseFloat(p.quantity) || 1;
        const uom = p.uom || 'PCS';
        const unitCost = parseFloat(String(p.value).replace(/[^0-9.]/g, '')) || 0;
        const extendedCost = qty * unitCost;
        subtotal += extendedCost;

        return `
            <tr>
                <td style="border: 2px solid #000; padding: 5px; text-align: center; font-size: 10px;">${qty}</td>
                <td style="border: 2px solid #000; padding: 5px; text-align: center; font-size: 10px;">${uom}</td>
                <td style="border: 2px solid #000; padding: 5px; font-size: 10px;">${p.description || 'No Description provided'}${p.madeOf ? ` (Made of: ${p.madeOf})` : ''}</td>
                <td style="border: 2px solid #000; padding: 5px; text-align: center; font-size: 10px;">${p.hsCode || 'N/A'}</td>
                <td style="border: 2px solid #000; padding: 5px; text-align: right; font-size: 10px;">${unitCost.toFixed(2)}</td>
                <td style="border: 2px solid #000; padding: 5px; text-align: right; font-size: 10px;">${extendedCost.toFixed(2)}</td>
            </tr>
        `;
    }).join('');

    // Fill empty rows to maintain layout (up to 10 rows)
    const emptyRowsCount = Math.max(0, 10 - products.length);
    const emptyRowsHTML = Array(emptyRowsCount).fill(0).map(() => `
        <tr>
            <td style="border: 2px solid #000; padding: 5px; height: 18px;"></td>
            <td style="border: 2px solid #000; padding: 5px;"></td>
            <td style="border: 2px solid #000; padding: 5px;"></td>
            <td style="border: 2px solid #000; padding: 5px;"></td>
            <td style="border: 2px solid #000; padding: 5px;"></td>
            <td style="border: 2px solid #000; padding: 5px;"></td>
        </tr>
    `).join('');

    const currencyLabel = currencyType || 'USD';
    const fValue = parseFloat(String(freightValue).replace(/[^0-9.]/g, '')) || 0;
    
    let insValue = 0;
    if (insuranceValue !== undefined && insuranceValue !== '' && insuranceValue !== null) {
        insValue = parseFloat(String(insuranceValue).replace(/[^0-9.]/g, '')) || 0;
    } else if (shipmentInsuranceDeclaredValue) {
        const insDeclared = parseFloat(String(shipmentInsuranceDeclaredValue).replace(/[^0-9.]/g, '')) || 0;
        if (insDeclared > 100) {
            insValue = Math.max(6.60, insDeclared * 0.01);
        }
    }
    
    const grandTotal = subtotal + fValue + insValue;

    return `
    <div class="page" style="page-break-after: always; width: 210mm; min-height: 297mm; padding: 10mm; margin: 0 auto; box-sizing: border-box; background: white; font-family: Arial, sans-serif; color: #000;">
        <div style="width: 100%; box-sizing: border-box;">
            
            <!-- Top Header Grid Section -->
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 25px; table-layout: fixed;">
                <tr>
                    <td colspan="2" rowspan="4" style="border: 2px solid #000; width: 33%;"></td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 14px; width: 15%; font-weight: bold;">Shipper</td>
                    <td colspan="2" style="border: 2px solid #000; width: 25%;"></td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 16px; font-weight: bold; width: 15%; text-align: center;">INVOICE</td>
                    <td style="border: 2px solid #000; width: 12%;"></td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">Name</td>
                    <td colspan="2" style="border: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold;">${shipper.name || ''}</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">Invoice No.</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold;">${displayInvoiceNo}</td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">Address</td>
                    <td colspan="2" style="border: 2px solid #000; padding: 4px; font-size: 11px;">${shipper.address1 || ''}</td>
                    <td style="border: 2px solid #000; padding: 4px;"></td>
                    <td style="border: 2px solid #000; padding: 4px;"></td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">City</td>
                    <td colspan="2" style="border: 2px solid #000; padding: 4px; font-size: 11px;">${shipper.city || ''}</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">Date</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold;">${date}</td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px; width: 10%;">Tax ID/EIN</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px; width: 23%; font-weight: bold;">${shipper.taxId || ''}</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">State</td>
                    <td colspan="2" style="border: 2px solid #000; padding: 4px; font-size: 11px;">${shipper.state || ''}</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">Currency</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold;">${currencyLabel}</td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">Email</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold; word-break: break-all;">${shipper.email || ''}</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">Postal Code</td>
                    <td colspan="2" style="border: 2px solid #000; padding: 4px; font-size: 11px;">${shipper.postalCode || ''}</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px;">${data.iossNumber ? 'IOSS No.' : ''}</td>
                    <td style="border: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold; color: #2b6cb0;">${data.iossNumber || ''}</td>
                </tr>
            </table>

            <!-- SHIP TO: Section -->
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
                <tr>
                    <td style="border: 2px solid #000; background: #fff; padding: 4px; font-size: 12px; font-weight: bold; text-transform: uppercase;">SHIP TO:</td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 0;">
                            <tr>
                                <td style="border-right: 2px solid #000; border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">Export Reason</td>
                                <td style="border-bottom: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold;">${exportReason || ''}</td>
                            </tr>
                            ${countryOfOrigin ? `
                            <tr>
                                <td style="border-right: 2px solid #000; border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">Country of Origin</td>
                                <td style="border-bottom: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold;">${countryOfOrigin}</td>
                            </tr>
                            ` : ''}
                            ${fValue > 0 ? `
                            <tr>
                                <td style="border-right: 2px solid #000; border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">Terms of Sale / Incoterm</td>
                                <td style="border-bottom: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold; color: #1a365d;">DAP (Delivered at Place)</td>
                            </tr>
                            ` : ''}
                        <table style="width: 100%; border-collapse: collapse;">
                            <tr>
                                <td style="width: 15%; border-right: 2px solid #000; border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">Company/Name</td>
                                <td style="border-bottom: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold;">${destination.name || destination.company || ''}</td>
                            </tr>
                            <tr>
                                <td style="border-right: 2px solid #000; border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">Address</td>
                                <td style="border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">${destination.address1 || ''} ${(destination.address2 || '')}</td>
                            </tr>
                            <tr>
                                <td style="border-right: 2px solid #000; border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">City</td>
                                <td style="border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">${destination.city || ''}</td>
                            </tr>
                            <tr>
                                <td style="border-right: 2px solid #000; border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">Postal Code</td>
                                <td style="border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">${destination.postalCode || ''}</td>
                            </tr>
                            <tr>
                                <td style="border-right: 2px solid #000; border-bottom: 2px solid #000; padding: 4px; font-size: 11px;">Country</td>
                                <td style="border-bottom: 2px solid #000; padding: 4px; font-size: 11px; font-weight: bold;">${destination.country || ''}</td>
                            </tr>
                            <tr>
                                <td style="border-right: 2px solid #000; padding: 4px; font-size: 11px;">Phone No.</td>
                                <td style="padding: 4px; font-size: 11px;">${destination.phone || ''}</td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </table>

            <div style="font-size: 12px; margin-bottom: 8px; font-weight: normal;">Order Information</div>
            
            <!-- Items Table -->
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 0;">
                <thead>
                    <tr style="font-size: 9px; text-align: center;">
                        <th style="border: 2px solid #000; padding: 4px; width: 40px;">Qty</th>
                        <th style="border: 2px solid #000; padding: 4px; width: 40px;">UOM</th>
                        <th style="border: 2px solid #000; padding: 4px;">Product Description: Detailed Description required, NOT part numbers (what is it, what is it made of, what is it used for)</th>
                        <th style="border: 2px solid #000; padding: 4px; width: 80px;">HTC / Schedule B</th>
                        <th style="border: 2px solid #000; padding: 4px; width: 70px;">Unit cost (${currencyLabel})</th>
                        <th style="border: 2px solid #000; padding: 4px; width: 80px;">Extended Cost (${currencyLabel})</th>
                    </tr>
                </thead>
                <tbody style="font-size: 10px;">
                    ${itemsHTML}
                    ${emptyRowsHTML}
                </tbody>
            </table>

            <!-- Footers Section -->
            <table style="width: 100%; border-collapse: collapse;">
                <tr>
                    <td rowspan="3" style="border: 2px solid #000; vertical-align: top; width: 70%; padding: 4px;">
                        <div style="font-size: 10px; font-weight: bold; border-bottom: 1px solid #000; padding-bottom: 4px;">Additional Comments</div>
                        <div style="padding-top: 10px; font-size: 11px; min-height: 80px;">
                            Tracking Number: <strong>${trackingNumber}</strong>
                            ${fValue > 0 ? `<div style="margin-top: 4px;">Incoterm: <strong>DAP (Delivered at Place)</strong></div>` : ''}
                        </div>
                    </td>
                    <td style="border: 2px solid #000; padding: 4px; text-align: right; width: 15%; font-size: 11px;">Subtotal:</td>
                    <td style="border: 2px solid #000; padding: 4px; text-align: right; width: 15%; font-size: 11px;">${currencyLabel} ${subtotal.toFixed(2)}</td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 4px; text-align: right; font-size: 11px;">Freight:</td>
                    <td style="border: 2px solid #000; padding: 4px; text-align: right; font-size: 11px;">${currencyLabel} ${fValue.toFixed(2)}</td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 4px; text-align: right; font-size: 11px;">Insurance:</td>
                    <td style="border: 2px solid #000; padding: 4px; text-align: right; font-size: 11px;">${currencyLabel} ${insValue.toFixed(2)}</td>
                </tr>
                <tr>
                    <td style="border: 2px solid #000; padding: 4px; text-align: right; font-weight: bold; font-size: 12px; background: #eee;">Grand Total:</td>
                    <td style="border: 2px solid #000; padding: 4px; text-align: right; font-weight: bold; font-size: 12px; background: #eee;">${currencyLabel} ${grandTotal.toFixed(2)}</td>
                </tr>
            </table>

            <div style="margin-top: 15px; font-size: 11px;">
                ${declarationStatement || 'I declare that all the information contained in this invoice is true and correct.'}
            </div>
        </div>
    </div>
    `;
};
